import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

interface DocumentCategory {
  name: string;
  slug: string;
  icon: string;
  subcategories: {
    name: string;
    slug: string;
  }[];
}

interface DocumentsDropdownProps {
  categories: DocumentCategory[];
  isOpen: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onClose: () => void;
}

export function DocumentsDropdown({ 
  categories, 
  isOpen, 
  onToggle, 
  onClose 
}: DocumentsDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useOnClickOutside(dropdownRef, () => {
    if (isOpen) onClose();
  });

  const handleSubmenuItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div className="relative group" ref={dropdownRef}>
      <div
        onClick={onToggle}
        className="flex items-center space-x-1 text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors focus:outline-none text-sm lg:text-base cursor-pointer"
      >
        <span>Documentos Oficiais</span>
        <ChevronDown
          size={16}
          className={`transform transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-300 group-hover:w-full"></span>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 animate-fade-in z-[60]">
          {categories.map((category) => (
            <div
              key={category.slug}
              className="group/item relative"
            >
              {category.subcategories.length > 0 ? (
                <>
                  <Link
                    to={`/documentos/${category.slug}`}
                    className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-100"
                    onClick={handleSubmenuItemClick}
                  >
                    {category.name}
                  </Link>
                  <div className="absolute left-full top-0 hidden group-hover/item:block">
                    <div className="ml-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 animate-fade-in z-[60]">
                      {category.subcategories.map((sub) => (
                        <Link
                          key={sub.slug}
                          to={`/documentos/${category.slug}/${sub.slug}`}
                          className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
                          onClick={handleSubmenuItemClick}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="block px-4 py-2 text-gray-400 dark:text-gray-500 cursor-not-allowed">
                  {category.name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}